import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Paper,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Avatar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  IconButton,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Button,
  Divider,
  styled,
} from "@mui/material";
import api from "../api/api";
import { FaCheckCircle, FaClock, FaCloudRain } from "react-icons/fa";
import {
  AgricultureOutlined,
  AssessmentOutlined,
  CheckCircle,
  FilterListOffOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Person,
  Recommend,
  RemoveCircle,
  Search,
} from "@mui/icons-material";
import moment from "moment";
import { useTranslation } from "react-i18next";
import "./AdminManagement.css";
import "./SuiviIrrigation.css";

// Custom theme with brand colors
const theme = createTheme({
  palette: {
    primary: {
      main: "#26a6b7",
      light: "#e8f7f9",
      dark: "#1d8290",
    },
    secondary: {
      main: "#e8f7f9",
    },
    success: {
      main: "#22c55e",
    },
    error: {
      main: "#ef4444",
    },
    warning: {
      main: "#f59e0b",
    },
    info: {
      main: "#3b82f6",
    },
    background: {
      default: "#fafbfc",
      paper: "#ffffff",
    },
    text: {
      primary: "#1a1a1a",
      secondary: "#666666",
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          background: "#ffffff",
          color: "#23364d",
          border: "1px solid #e5ebef",
          boxShadow: "0 10px 28px rgba(18, 68, 84, .07)",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            background: "#f3fafb",
            color: "#1d8290",
            fontWeight: 600,
            fontSize: "0.9rem",
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(38, 166, 183, 0.04)",
          },
          "&:nth-of-type(even)": {
            backgroundColor: "rgba(38, 166, 183, 0.02)",
          },
        },
      },
    },
  },
});

const DashboardContainer = styled("div")({
  width: "100%",
  maxWidth: "none",
  margin: "0 auto",
  padding: "16px",
});

const CompactCard = styled(Card)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(1.5),
  background: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderRadius: "12px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  transition: "transform 0.2s",
  "&:hover": {
    transform: "translateY(-2px)",
  },
}));

const StatIconBubble = styled(Box)(({ tint, text }) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 6px",
  background: tint,
  color: text,
}));

const Suivi_irrigation = () => {

    const { t } = useTranslation();
  const statusItems = [
    {
      label: "Validated",
      icon: <CheckCircle sx={{ fontSize: 16 }} />,
      tint: "#e8f7f9",
      text: "#137a56",
    },
    {
      label: 'Refused',
      icon: <RemoveCircle sx={{ fontSize: 16 }} />,
      tint: "#fdeceb",
      text: "#b83d53",
    },
    {
      label: "Waiting",
      icon: <FaClock size={14} />,
      tint: "#fff7e6",
      text: "#9a6410",
    },
  ];

  const sourceItems = [
    {
      label: "Rain",
      icon: <FaCloudRain size={14} />,
      tint: "#e8f1fd",
      text: "#1d4ed8",
    },
    {
      label: "Manual",
      icon: <Person sx={{ fontSize: 16 }} />,
      tint: "#f1ecfd",
      text: "#7c3aed",
    },
    {
      label: "Recommendation",
      icon: <Recommend sx={{ fontSize: 16 }} />,
      tint: "#fdecf3",
      text: "#db2777",
    },
  ];


  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grouped");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState("fieldName");
  const [order, setOrder] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedField, setSelectedField] = useState("all");
  const [selectedSource, setSelectedSource] = useState("all");
  const [minDose, setMinDose] = useState("");
  const [maxDose, setMaxDose] = useState("");

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await api.get(`/field/get-events`);
        setEvents(response.data.events || []);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const uniqueFields = useMemo(() => {
    return [
      ...new Set(events.map((e) => e.field?.name || "Unknown Field")),
    ].sort();
  }, [events]);

  const uniqueSources = useMemo(() => {
    return [...new Set(events.map((e) => e.source || "Unknown"))].sort();
  }, [events]);

  // Filter events first
  const filteredEvents = useMemo(() => {
    let data = events;

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      data = data.filter(
        (event) =>
          (event.field?.name || "").toLowerCase().includes(lowerSearch) ||
          (event.status || "").toLowerCase().includes(lowerSearch) ||
          (event.source || "").toLowerCase().includes(lowerSearch) ||
          (event.dose ?? "").toString().toLowerCase().includes(lowerSearch) ||
          (event.start || "").toLowerCase().includes(lowerSearch) ||
          (event.time || "").toLowerCase().includes(lowerSearch)
      );
    }

    if (statusFilter !== "all") {
      if (statusFilter === "rain") {
        data = data.filter((event) => event.source?.toLowerCase() === "rain");
      } else {
        data = data.filter(
          (event) => event.status?.toLowerCase() === statusFilter
        );
      }
    }

    if (fromDate) {
      data = data.filter((event) => event.start >= fromDate);
    }

    if (toDate) {
      data = data.filter((event) => event.start <= toDate);
    }

    if (selectedField !== "all") {
      data = data.filter((event) => event.field?.name === selectedField);
    }

    if (selectedSource !== "all") {
      data = data.filter((event) => event.source === selectedSource);
    }

    if (minDose) {
      const min = parseFloat(minDose);
      data = data.filter((event) => parseFloat(event.dose) >= min);
    }

    if (maxDose) {
      const max = parseFloat(maxDose);
      data = data.filter((event) => parseFloat(event.dose) <= max);
    }

    return data;
  }, [
    events,
    searchTerm,
    statusFilter,
    fromDate,
    toDate,
    selectedField,
    selectedSource,
    minDose,
    maxDose,
  ]);

  // Group filtered events by field name
  const groupedData = useMemo(() => {
    return filteredEvents.reduce((acc, event) => {
      const fieldName = event.field?.name || "Unknown Field";

      if (!acc[fieldName]) {
        acc[fieldName] = {
          id: fieldName,
          fieldName,
          events: [],
          validated: 0,
          refused: 0,
          waiting: 0,
          rain: 0,
          total: 0,
        };
      }

      acc[fieldName].events.push(event);
      acc[fieldName].total += 1;

      const status = event.status?.toLowerCase();
      if (status === "validated") acc[fieldName].validated += 1;
      else if (status === "refused") acc[fieldName].refused += 1;
      else if (status === "waiting") acc[fieldName].waiting += 1;

      if (event.source?.toLowerCase() === "rain") acc[fieldName].rain += 1;

      return acc;
    }, {});
  }, [filteredEvents]);

  // Statistics calculations based on filtered events
  const stats = useMemo(() => {
    return {
      total: filteredEvents.length,
      validated: filteredEvents.filter(
        (e) => e.status?.toLowerCase() === "validated"
      ).length,
      refused: filteredEvents.filter(
        (e) => e.status?.toLowerCase() === "refused"
      ).length,
      waiting: filteredEvents.filter(
        (e) => e.status?.toLowerCase() === "waiting"
      ).length,
      rain: filteredEvents.filter((e) => e.source?.toLowerCase() === "rain")
        .length,
      recommendation: filteredEvents.filter(
        (e) => e.source?.toLowerCase() === "recommendation"
      ).length,
      manual: filteredEvents.filter((e) => e.source?.toLowerCase() === "manual")
        .length,
    };
  }, [filteredEvents]);

  // Sort data
  const sortedData = useMemo(() => {
    let data = viewMode === "grouped" ? Object.values(groupedData) : [...filteredEvents];

    // Sort data
    data.sort((a, b) => {
      let aValue, bValue;

      if (viewMode === "grouped") {
        switch (orderBy) {
          case "validationPercentage":
            aValue = a.total > 0 ? a.validated / a.total : 0;
            bValue = b.total > 0 ? b.validated / b.total : 0;
            break;
          case "fieldName":
            aValue = a.fieldName;
            bValue = b.fieldName;
            break;
          default:
            aValue = a[orderBy] || 0;
            bValue = b[orderBy] || 0;
        }
      } else {
        switch (orderBy) {
          case "fieldName":
            aValue = a.field?.name || "";
            bValue = b.field?.name || "";
            break;
          default:
            aValue = a[orderBy] || "";
            bValue = b[orderBy] || "";
        }
      }

      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return order === "asc" ? aValue - bValue : bValue - aValue;
    });

    return data;
  }, [filteredEvents, groupedData, viewMode, orderBy, order]);

  // Handle sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Handle row expansion
  const handleExpandClick = (fieldName) => {
    setExpandedRows((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  // Pagination
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Status component with icons
  const StatusChip = ({ status }) => {
    const getStatusConfig = (status) => {
      switch (status?.toLowerCase()) {
        case "validated":
          return {
            icon: <FaCheckCircle size={16} />,
            color: "success",
            label: "validated",
          };
        case "refused":
          return {
            icon: <RemoveCircle sx={{ fontSize: 14 }} />,
            color: "error",
            label: "Refused",
          };
        case "waiting":
          return {
            icon: <FaClock size={16} />,
            color: "warning",
            label: "Waiting",
          };
        case "rain":
          return {
            icon: <FaCloudRain size={16} />,
            color: "info",
            label: "Rain",
          };
        default:
          return {
            icon: <FaClock size={16} />,
            color: "default",
            label: status || "Unknown",
          };
      }
    };

    const config = getStatusConfig(status);

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        variant="filled"
        size="small"
        sx={{ fontWeight: 500 }}
      />
    );
  };

  // Validation Percentage Component
  const ValidationPercentage = ({ validated, total }) => {
    const percentage = total > 0 ? Math.round((validated / total) * 100) : 0;

    const getColor = (percentage) => {
      if (percentage >= 80) return "success";
      if (percentage >= 50) return "warning";
      return "error";
    };

    return (
      <Box sx={{ minWidth: 120 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={0.5}
        >
          <Typography variant="body2" fontWeight="bold">
            {percentage}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {validated}/{total}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={percentage}
          color={getColor(percentage)}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: "#e8eaed",
          }}
        />
      </Box>
    );
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Box className="irrigation-loading" textAlign="center">
            <LinearProgress sx={{ width: 200, mb: 2 }} />
            <Typography>{t("Loading irrigation events...")}</Typography>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        className="admin-management-page irrigation-management-page"
        sx={{
          p: { xs: 1, sm: 2, md: 3 },
          background: "linear-gradient(135deg, #fafbfc 0%, #e8f7f9 100%)",
          minHeight: "100vh",
        }}
      >
        {/* Header */}
        <Box mb={4}>
          <Typography
            className="irrigation-page-title"
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: "bold",
              color: "#1d8290",
              textAlign: "center",
            }}
          >
            {t("Irrigation Events Tracking")}
          </Typography>

          {/* View Mode Toggle */}
          <Box className="irrigation-view-switcher" display="flex" justifyContent="center" mt={2}>
            <Stack direction="row" spacing={2}>
              <Chip
                icon={<AssessmentOutlined />}
                label={t("Grouped Summary")}
                clickable
                color={viewMode === "grouped" ? "primary" : "default"}
                variant={viewMode === "grouped" ? "filled" : "outlined"}
                onClick={() => setViewMode("grouped")}
              />
              <Chip
                icon={<FilterListOffOutlined />}
                label={t("Detailed View")}
                clickable
                color={viewMode === "detailed" ? "primary" : "default"}
                variant={viewMode === "detailed" ? "filled" : "outlined"}
                onClick={() => setViewMode("detailed")}
              />
            </Stack>
          </Box>
        </Box>

        <DashboardContainer className="d-flex justify-content-center">
          <Grid container spacing={2}>
            {/* Total Events - More compact */}

            {/* Status and Source in one row on larger screens */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, pl: 1 }}
                color="textSecondary"
              >
                {t("STATUS")}
              </Typography>
              <Grid container spacing={1}>
                {statusItems.map((item) => (
                  <Grid item xs={4} key={item.label}>
                    <CompactCard className="irrigation-stat-card">
                      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                        <StatIconBubble tint={item.tint} text={item.text}>
                          {item.icon}
                        </StatIconBubble>
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
  {t(item.label)}
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: item.text }}>
                          {stats[item.label.toLowerCase()]}
                        </Typography>
                      </CardContent>
                    </CompactCard>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <CompactCard sx={{ mb: 2 }}>
                <CardContent sx={{ py: 1 }}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {t("TOTAL EVENTS")}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                </CardContent>
              </CompactCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, pl: 1 }}
                color="textSecondary"
              >
                {t("SOURCE")}
              </Typography>
              <Grid container spacing={1}>
                {sourceItems.map((item) => (
                  <Grid item xs={4} key={item.label}>
                    <CompactCard className="irrigation-stat-card">
                      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                        <StatIconBubble tint={item.tint} text={item.text}>
                          {item.icon}
                        </StatIconBubble>
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
  {t(item.label)}
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: item.text }}>
                          {stats[item.label.toLowerCase()]}
                        </Typography>
                      </CardContent>
                    </CompactCard>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DashboardContainer>
        {/* Table with Custom Toolbar */}
        <Paper
          className="irrigation-data-card"
          elevation={3}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "rgba(38, 166, 183, 0.15) 0px 8px 32px",
          }}
        >
          {/* Custom Toolbar */}
          <Toolbar
            className="irrigation-toolbar"
            sx={{ background: "#fafbfc", borderBottom: "1px solid #e8eaed" }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  size="small"
                  placeholder={t("Search...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>{t("Filter by Status")}</InputLabel>
                  <Select
                    value={statusFilter}
                    label={t("Filter by Status")}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">{t("All Status")}</MenuItem>
                    <MenuItem value="validated">{t("Validated")}</MenuItem>
                    <MenuItem value="refused">{t("Refused")}</MenuItem>
                    <MenuItem value="waiting">{t("Waiting")}</MenuItem>
                    <MenuItem value="rain">{t("rain")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <Button
                    startIcon={<FilterListOffOutlined />}
                    onClick={() => setShowFilters(!showFilters)}
                    variant={showFilters ? "contained" : "outlined"}
                    size="small"
                  >
                    {t("Filters")}
                  </Button>
                  {/* <Button
                    startIcon={<DownloadDone />}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // Export functionality would go here
                      console.log('Export data', sortedData);
                    }}
                  >
                    Export
                  </Button> */}
                </Box>
              </Grid>
            </Grid>
          </Toolbar>

          {/* Advanced Filters */}
          <Collapse in={showFilters}>
            <Box sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
              <Typography variant="subtitle1" gutterBottom>
                {t("Advanced Filters")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("From Date")}
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("To Date")}
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("Field")}</InputLabel>
                    <Select
                      value={selectedField}
                      label={t("Field")}
                      onChange={(e) => setSelectedField(e.target.value)}
                    >
                      <MenuItem value="all">{t("All Fields")}</MenuItem>
                      {uniqueFields.map((field) => (
                        <MenuItem key={field} value={field}>
                          {field}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("Source")}</InputLabel>
                    <Select
                      value={selectedSource}
                      label={t("Source")}
                      onChange={(e) => setSelectedSource(e.target.value)}
                    >
                      <MenuItem value="all">{t("All Sources")}</MenuItem>
                      {uniqueSources.map((source) => (
                        <MenuItem key={source} value={source}>
                          {source}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("Min Dose")}
                    type="number"
                    value={minDose}
                    onChange={(e) => setMinDose(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("Max Dose")}
                    type="number"
                    value={maxDose}
                    onChange={(e) => setMaxDose(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>

          {/* Table */}
          <TableContainer className="irrigation-table-container">
            <Table className="irrigation-table">
              <TableHead>
                <TableRow>
                  {viewMode === "grouped" ? (
                    <>
                      <TableCell></TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "fieldName"}
                          direction={orderBy === "fieldName" ? order : "asc"}
                          onClick={() => handleSort("fieldName")}
                        >
                          {t("Field Name")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "validationPercentage"}
                          direction={
                            orderBy === "validationPercentage" ? order : "asc"
                          }
                          onClick={() => handleSort("validationPercentage")}
                        >
                          {t("Validation %")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "total"}
                          direction={orderBy === "total" ? order : "asc"}
                          onClick={() => handleSort("total")}
                        >
                          {t("TOTAL EVENTS")}
                        </TableSortLabel>
                      </TableCell>
<TableCell>{t("Validated")}</TableCell>
<TableCell>{t("Refused")}</TableCell>
<TableCell>{t("Waiting")}</TableCell>
<TableCell>{t("Rain")}</TableCell>

                    </>
                  ) : (
                    <>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "fieldName"}
                          direction={orderBy === "fieldName" ? order : "asc"}
                          onClick={() => handleSort("fieldName")}
                        >
                          {t("Field Name")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "status"}
                          direction={orderBy === "status" ? order : "asc"}
                          onClick={() => handleSort("status")}
                        >
                          {t("Status")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "dose"}
                          direction={orderBy === "dose" ? order : "asc"}
                          onClick={() => handleSort("dose")}
                        >
                          {t("Dose")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "start"}
                          direction={orderBy === "start" ? order : "asc"}
                          onClick={() => handleSort("start")}
                        >
                          {t("Day")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "duration"}
                          direction={orderBy === "duration" ? order : "asc"}
                          onClick={() => handleSort("duration")}
                        >
                          {t("Duration")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "source"}
                          direction={orderBy === "source" ? order : "asc"}
                          onClick={() => handleSort("source")}
                        >
                          {t("Source")}
                        </TableSortLabel>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row, index) => (
                  <React.Fragment
                    key={
                      viewMode === "grouped" ? row.fieldName : row.id || index
                    }
                  >
                    <TableRow>
                      {viewMode === "grouped" ? (
                        <>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleExpandClick(row.fieldName)}
                            >
                              {expandedRows[row.fieldName] ? (
                                <KeyboardArrowUp />
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar
                                sx={{
                                  bgcolor: "primary.main",
                                  width: 32,
                                  height: 32,
                                }}
                              >
                                <AgricultureOutlined fontSize="small" />
                              </Avatar>
                              <Typography variant="body2" fontWeight="bold">
                                {row.fieldName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <ValidationPercentage
                              validated={row.validated}
                              total={row.total}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.total}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {row.validated > 0 && (
                              <Chip
                                icon={<CheckCircle sx={{ fontSize: 14 }} />}
                                label={row.validated}
                                color="success"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {row.refused > 0 && (
                              <Chip
                                icon={<RemoveCircle sx={{ fontSize: 14 }} />}
                                label={row.refused}
                                color="error"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {row.waiting > 0 && (
                              <Chip
                                icon={<FaClock size={14} />}
                                label={row.waiting}
                                color="warning"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {row.rain > 0 && (
                              <Chip
                                icon={<FaCloudRain size={14} />}
                                label={row.rain}
                                color="info"
                                size="small"
                              />
                            )}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar
                                sx={{
                                  bgcolor: "primary.main",
                                  width: 32,
                                  height: 32,
                                }}
                              >
                                <AgricultureOutlined fontSize="small" />
                              </Avatar>
                              <Typography variant="body2" fontWeight="bold">
                                {row.field?.name || "Unknown Field"}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={row.status} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {row.dose || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.start || "-"}
                            </Typography>
                          </TableCell>
                                                    <TableCell>
                            <Typography variant="body2">
                              {Math.round(row.time) || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.source || "-"}
                            </Typography>
                          </TableCell>
                        </>
                      )}
                    </TableRow>

                    {/* Expanded row for grouped view */}
                    {viewMode === "grouped" && expandedRows[row.fieldName] && (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0 }}>
                          <Collapse
                            in={expandedRows[row.fieldName]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                              <Typography variant="h6" gutterBottom>
                                {t("Detailed Events of this week for")} {row.fieldName}
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>
                                      <strong>{t("Status")}</strong>
                                    </TableCell>
                                    <TableCell>
                                      <strong>{t("Dose")}</strong>
                                    </TableCell>
                                    <TableCell>
                                      <strong>{t("Day")}</strong>
                                    </TableCell>
                                                                        <TableCell>
                                      <strong>{t("Duration")}</strong>
                                    </TableCell>
                                    <TableCell>
                                      <strong>{t("Source")}</strong>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {row.events
                                    .filter((event) => {
                                      const eventDate = moment(
                                        event.start,
                                        "YYYY-MM-DD"
                                      ); // parse your event date
                                      const startOfWeek =
                                        moment().startOf("week"); // Sunday or Monday depending on locale
                                      const endOfWeek = moment().endOf("week");
                                      return eventDate.isBetween(
                                        startOfWeek,
                                        endOfWeek,
                                        null,
                                        "[]"
                                      ); // inclusive
                                    })
                                    .map((event, eventIndex) => (
                                      <TableRow key={eventIndex}>
                                        <TableCell>
                                          <StatusChip status={event.status} />
                                        </TableCell>
                                        <TableCell>
                                          {event.dose || "-"}
                                        </TableCell>
                                        <TableCell>
                                          {event.start || "-"}
                                        </TableCell>
                                                  <TableCell>
                                          {event.time || "-"}
                                        </TableCell>
                                        <TableCell>
                                          {event.source || "-"}
                                        </TableCell>
                                    
                                      </TableRow>
                                      
                                    ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid #e8eaed",
              backgroundColor: "#fafbfc",
            }}
          />
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

export default Suivi_irrigation;
